import {Component, forwardRef, Input, ViewChild, ElementRef, HostListener} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {noop} from '@angular/core/src/linker/view_utils';
import {NgbInputDatepicker} from '@ng-bootstrap/ng-bootstrap/datepicker/datepicker-input';
import {NgbDateParserFormatter, NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {AuthService} from '../../../security/authorization/auth.service';
import {Roles} from '../../../../domain/authorization/roles';
import {coerceBooleanProperty} from '../../../../common/value/coercion/boolean-coercion.util';
import moment = require('moment');
import Moment = moment.Moment;
import isEqual = require('lodash/isEqual');
import isString = require('lodash/isString');
import padStart = require('lodash/padStart');

// region NG_VALUE_ACCESSOR
const MY_DATEPICKER_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DatepickerComponent),
    multi: true
};
// endregion

const DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const DATE_MASK = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];

let nextUniqueId = 0;

/**
 * Created by Rodrigo Quesada on 10/12/16.
 */
@Component({
    selector: 'my-datepicker',
    template: require('./datepicker.component.html'),
    styles: [require('./datepicker.component.scss')],
    providers: [MY_DATEPICKER_CONTROL_VALUE_ACCESSOR]
})
export class DatepickerComponent implements ControlValueAccessor {

    readonly id = `my-datepicker-${++nextUniqueId}`;

    // region Input
    @Input() placeholder: string;
    @Input() name: string|null = null;
    @Input() errorMessage: string;
    @Input() errorMessagePosition: string = 'right';

    _maxDate: NgbDateStruct = toNgbDateStruct(moment().add(10, 'year'));
    @Input() set maxDate(value: Moment) {
        this._maxDate = toNgbDateStruct(value);
    }

    _minDate: NgbDateStruct = toNgbDateStruct(moment().subtract(10, 'year'));
    @Input() set minDate(value: Moment) {
        this._minDate = toNgbDateStruct(value);
    }

    private _readonly: boolean = true;

    @Input()
    get readonly(): boolean {
        return this._readonly;
    }

    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
    }

    private _disabled: boolean = false;

    @Input() get disabled(): boolean {
        return this._disabled;
    }

    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
    }

    // endregion

    // region Children
    @ViewChild(NgbInputDatepicker) private _ngbInputDatepicker: NgbInputDatepicker;

    @ViewChild('input') private _inputElement: ElementRef;
    // endregion

    private _value?: NgbDateStruct;

    get value(): string|NgbDateStruct|undefined {
        return this._value;
    };

    set value(value: string|NgbDateStruct|undefined) {
        let newValue: NgbDateStruct|undefined = isString(value) ? parseValue(value) : value;
        if (!isEqual(this._value, newValue)) {
            this._value = newValue;
            this._onChangeCallback();
        }
    }

    private focused: boolean = false;

    get datePattern() {
        return DATE_REGEX;
    }

    get dateMask() {
        return DATE_MASK;
    }

    get empty() {
        return this._inputElement.nativeElement.value === '';
    }

    constructor(private _el: ElementRef, private _authService: AuthService) {
        this._authService.user$.take(1).subscribe(user => {
            if (user && user.hasAnyRole([Roles.ADMIN])) {
                this._readonly = false;
            }
        });
    }

    // region Events
    onFocus() {
        this.focused = true;
        this._ngbInputDatepicker.open();
    }

    onBlur() {
        this.focused = false;
        this._onTouchedCallback();
    }

    handleChange = () => {
        this.value = this._inputElement.nativeElement.value;
        this._onTouchedCallback();
    }

    clickOutside(event) {
        this._ngbInputDatepicker.close();
    }

    clearDate() {
        this.value = undefined;
    }

    @HostListener('keydown.tab', ['$event'])
    onTab(event: KeyboardEvent) {
        event.preventDefault();
        this._ngbInputDatepicker.close();
        this.nextInputElement().focus();
    }

    private nextInputElement() {
        return $(this._el.nativeElement)
            .nextAll()
            .map((i, s) => $(s).find('input').first()[0])
            .first();
    }

    // endregion

    // region ControlValueAccessor
    /** Callback registered via registerOnTouched (ControlValueAccessor) */
    private _onTouchedCallback: () => void = noop;
    /** Callback registered via registerOnChange (ControlValueAccessor) */
    private _onChangeCallback: () => void = noop;

    writeValue(value: Moment|null|undefined): void {
        this._value = value ? toNgbDateStruct(value) : undefined;
    }

    registerOnChange(fn: any): void {
        this._onChangeCallback = () => fn(this._value ? toMoment(this._value) : undefined);
    }

    registerOnTouched(fn: any): void {
        this._onTouchedCallback = fn;
    }

    // endregion
}

function toMoment(value: NgbDateStruct): Moment {
    value = {...value};
    --value.month;
    return moment.utc(value);
}

function toNgbDateStruct(value: Moment): NgbDateStruct {
    return {
        month: value.month() + 1,
        day: value.date(),
        year: value.year()
    };
}

function parseValue(value: string): NgbDateStruct|undefined {
    let match = DATE_REGEX.exec(value);
    return match ? {
            month: +match[1],
            day: +match[2],
            year: +match[3]
        } : undefined;
}

function padNumber(value: number, length: number): string {
    return padStart(value.toString(), length, '0');
}

export class CustomNgbDateParserFormatter extends NgbDateParserFormatter {

    parse(value: string): NgbDateStruct {
        return parseValue(value) || null as any;
    }

    format(date: NgbDateStruct|null|undefined): string {
        return date ? `${padNumber(date.month, 2)}/${padNumber(date.day, 2)}/${padNumber(date.year, 4)}` : '';
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/components/forms/controls/datepicker/datepicker.component.ts